import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { Button } from '../ui';
import { isChunkLoadError } from './chunkLoadError';
import { FailureSurface } from './FailureSurface';
import { RouteTitle } from './RouteTitle';
import { reportError } from './telemetry';

export interface RouteErrorElementProps {
    /**
     * What the error screen offers. `home` (the default) navigates to the dashboard, for a failure
     * inside the signed-in app. `reload` reloads the page, for the routes where going home would land
     * the user back on the same failure: the sign-in page and the signed-in check around the whole app
     * (BUG-053). A chunk that failed to load is the usual cause there — a tab left open across a
     * deploy asks for a file the new deploy no longer has — and only a real reload fetches the new one.
     * Under `home`, a chunk that failed to load still offers the reload, in the same layout (BUG-139):
     * going home and back would ask for the same missing file.
     */
    readonly recovery?: 'home' | 'reload';
    /** What failed, completing "Couldn't load …". */
    readonly what?: string;
}

/**
 * The router's error element. Distinct from the render boundary because the router catches a
 * different class of failure — a route that cannot resolve, or a lazy chunk that fails to load —
 * and those must not reach the top-level boundary, which would force a full reload for something
 * navigable.
 *
 * A 404 is not an error worth apologising for, so it says something different from a real fault.
 *
 * Every layout carries the page's one `<main>` (design-system §9): each use replaces a whole page — the
 * sign-in page, the signed-in check, or, under the `/` route, `AppLayout` with its sidebar and its own
 * `<main>` (BUG-200).
 */
export function RouteErrorElement({ recovery = 'home', what = 'this page' }: RouteErrorElementProps) {
    const error = useRouteError();
    const navigate = useNavigate();
    const missing = isRouteErrorResponse(error) && error.status === 404;
    const reloads = recovery === 'reload' || isChunkLoadError(error);

    if (!missing) {
        reportError({ error, where: 'router' });
    }

    const goHome = (
        <Button
            variant="secondary"
            // The label is chosen at run time, so the testid scan cannot read it as visible text.
            data-testid={reloads ? 'app-error-reload' : 'app-error-home'}
            onClick={() => {
                if (reloads) {
                    window.location.reload();
                } else {
                    void navigate('/');
                }
            }}
        >
            {reloads ? 'Reload the page' : 'Back to the dashboard'}
        </Button>
    );

    if (recovery === 'reload') {
        // Rendered in place of a whole page (the sign-in page, or the app inside the signed-in check),
        // so it carries the page's one `<main>`. Every failure takes the reload here, a 404 included:
        // nothing below these routes answers one, and a reload is the recovery either way.
        return (
            <main>
                <RouteTitle />
                <FailureSurface what={what} action={goHome} />
            </main>
        );
    }

    if (missing) {
        return (
            <main>
                <div
                    role="alert"
                    style={{
                        margin: 'var(--et-space-8) auto',
                        maxWidth: 'var(--et-layout-message-max)',
                        padding: 'var(--et-space-7)',
                    }}
                >
                    <RouteTitle />
                    <h1
                        style={{
                            marginTop: 0,
                            fontFamily: 'var(--et-type-title-face)',
                            fontSize: 'var(--et-type-title-size)',
                            fontWeight: 'var(--et-type-title-weight)',
                            lineHeight: 'var(--et-type-title-line)',
                            letterSpacing: 'var(--et-type-title-tracking)',
                            color: 'var(--et-text-heading)',
                        }}
                    >
                        That page doesn't exist.
                    </h1>
                    <p style={{ color: 'var(--et-text-secondary)' }}>
                        The link may be out of date. Nothing is wrong with your account.
                    </p>
                    {goHome}
                </div>
            </main>
        );
    }

    // This element replaces the route's own, and with it the layout's `RouteTitle` and `<main>`, so it
    // carries both.
    return (
        <main>
            <RouteTitle />
            <FailureSurface what={what} action={goHome} />
        </main>
    );
}
